/**
 * Session token store of the Bearer clients: the native app and the Office
 * add-in (`./bearer-session.ts`).
 *
 * Both authenticate with the opaque server-side session id (see
 * `docs/plans/capacitor-mobile-app.md`, "Option A"). It is kept in the
 * client's credential store (the OS keychain in the app, the pane's storage in
 * Office, see `./credential-store.ts`) and mirrored in memory for synchronous
 * access on the request hot path.
 *
 * The keychain plugin is imported lazily by the store, so this module — pulled
 * in by `lib/api.ts`, which is imported almost everywhere — carries no native
 * dependency on the web or in tests. `getNativeToken()` is synchronous and
 * touches only the in-memory mirror.
 */
import { usesBearerSession } from './bearer-session';
import { credentialStore } from './credential-store';

const TOKEN_KEY = 'session_id';
/**
 * The app's long-lived credential, the counterpart to the browser's 30-day
 * refresh cookie. The session id alone resolves against Redis, which runs as a
 * cache with no persistence — every restart of it logged every app user out
 * while the web self-healed from its cookies. With this the app re-mints a
 * session silently instead (`renewNativeSession` in ./auth.ts, KAN-316).
 */
const REFRESH_KEY = 'refresh_token';

// The first native plugin call on Android can stall while the Capacitor bridge
// finishes wiring up. Loading the token must therefore NEVER block app startup
// (awaiting it at bootstrap froze the whole app on a white screen). Instead we
// memoize a single load promise, cap it with a timeout, and let the /me
// bootstrap await it — so a slow keychain delays only the first authenticated
// request, not first paint.
const ATTEMPT_TIMEOUT_MS = 1500;
const MAX_ATTEMPTS = 3;

let cachedToken: string | null = null;
let cachedRefreshToken: string | null = null;
let loadPromise: Promise<string | null> | null = null;

/** In-memory session id, or null when not logged in. Synchronous by design. */
export function getNativeToken(): string | null {
  return cachedToken;
}

/**
 * Authorization header for a Bearer client, or `{}` on the web / when logged out.
 * Use this in raw fetch/XHR calls that bypass `fetchWithAuth` (file upload,
 * transcription, …) so they carry the Bearer token in the app and the pane.
 */
export function getNativeAuthHeader(): Record<string, string> {
  if (!usesBearerSession()) return {};
  const token = getNativeToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const TIMED_OUT = Symbol('timeout');

/**
 * One keychain read. Resolves the value (or null if the key is absent) fast;
 * THROWS on timeout so the caller can distinguish "no token" (fast null) from
 * "bridge stalled" (timeout) and retry only the latter.
 */
async function readKeychainOnce(): Promise<string | null> {
  const raced = await Promise.race([
    credentialStore().get(TOKEN_KEY),
    sleep(ATTEMPT_TIMEOUT_MS).then(() => TIMED_OUT),
  ]);
  if (raced === TIMED_OUT) throw new Error('keychain-timeout');
  return typeof raced === 'string' ? raced : null;
}

async function doLoad(): Promise<string | null> {
  if (!usesBearerSession()) return null;
  // The first native plugin call can stall while the Android bridge wires up.
  // Retry the timeout case a few times so a transient early stall self-heals;
  // a genuine "no token" returns fast on the first attempt.
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    try {
      cachedToken = await readKeychainOnce();
      return cachedToken;
    } catch {
      await sleep(400);
    }
  }
  cachedToken = null;
  return null;
}

/**
 * Ensure the persisted session id is loaded into memory (idempotent, memoized).
 * NEVER call this on the render-blocking path — await it lazily (post-mount,
 * bridge ready) before the first authenticated request. If it resolves null
 * (no token / gave up), the memo is cleared so a later call retries.
 */
export function ensureTokenLoaded(): Promise<string | null> {
  if (!usesBearerSession()) return Promise.resolve(null);
  if (cachedToken) return Promise.resolve(cachedToken);
  if (!loadPromise) {
    loadPromise = doLoad().then((v) => {
      if (!v) loadPromise = null; // allow a later retry (stall or not-yet-logged-in)
      return v;
    });
  }
  return loadPromise;
}

/** Back-compat alias — prefer `ensureTokenLoaded`. */
export function loadStoredToken(): Promise<string | null> {
  return ensureTokenLoaded();
}

/** Persist the session id to the keychain and mirror it in memory. */
export async function persistToken(token: string): Promise<void> {
  cachedToken = token;
  loadPromise = Promise.resolve(token); // keep ensureTokenLoaded() consistent
  try {
    await credentialStore().set(TOKEN_KEY, token);
  } catch {
    // Keep the in-memory token even if persistence fails; the session still
    // works for this app run and re-login re-persists it.
  }
}

/** Persist the long-lived refresh token beside the session id. */
export async function persistRefreshToken(token: string): Promise<void> {
  cachedRefreshToken = token;
  try {
    await credentialStore().set(REFRESH_KEY, token);
  } catch {
    // In-memory copy still carries this app run; a re-login re-persists it.
  }
}

/**
 * The stored refresh token, read from the keychain on first use.
 *
 * Unlike the session id this is NOT on the request hot path — it is only
 * touched when a request came back 401 — so it can afford to be async without
 * the memoized fast path the session id needs.
 */
export async function getNativeRefreshToken(): Promise<string | null> {
  if (!usesBearerSession()) return null;
  if (cachedRefreshToken) return cachedRefreshToken;
  try {
    // Capped like the session-id read above, and for the same reason: the
    // first native plugin call can stall while the Capacitor bridge wires up.
    // This one is awaited by the 401 handler, so an unbounded wait would hang
    // the request that triggered it rather than fail it.
    const raced = await Promise.race([
      credentialStore().get(REFRESH_KEY),
      sleep(ATTEMPT_TIMEOUT_MS).then(() => TIMED_OUT),
    ]);
    if (raced === TIMED_OUT) {
      // Not cached as null: a later attempt (once the bridge is warm) must be
      // able to find a token that is actually there.
      console.warn('[native-token] keychain read timed out, treating as absent');
      return null;
    }
    cachedRefreshToken = typeof raced === 'string' ? raced : null;
  } catch {
    cachedRefreshToken = null;
  }
  return cachedRefreshToken;
}

/**
 * Drop the session id from memory and the keychain (explicit logout only).
 *
 * The refresh token goes with it: leaving it behind would let the app renew
 * itself straight back into the session the user just left.
 */
export async function clearNativeToken(): Promise<void> {
  cachedToken = null;
  cachedRefreshToken = null;
  loadPromise = Promise.resolve(null);
  try {
    const store = credentialStore();
    await store.remove(TOKEN_KEY);
    await store.remove(REFRESH_KEY);
  } catch {
    // Ignore — nothing to clear or plugin unavailable.
  }
}
